import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import { useAgent } from '@/auth/use-agent';
import { fetchIssueOptions, fetchOverview, fetchPlayers, fetchResults, issueLink } from './limited-links';
import { fetchWheelSpins } from './daily-wheel';
import { fetchWhales } from './whales';
import { fetchTrafficQuality } from './traffic-quality';
import type { IssueLinkBody, WheelSpinsFilters, WhalesFilters } from './types';

// Reports change when an agent issues or a player redeems; a minute of staleness is fine and
// keeps a click between pages from re-reading the warehouse.
const REPORT_STALE_MS = 60_000;

export function useOverview() {
  const { key } = useAgent();
  return useQuery({
    queryKey: ['overview', key],
    queryFn: ({ signal }) => fetchOverview(key, signal),
    staleTime: REPORT_STALE_MS,
  });
}

export function useIssueOptions() {
  const { key } = useAgent();
  return useQuery({
    queryKey: ['issue-options', key],
    queryFn: ({ signal }) => fetchIssueOptions(key, signal),
    staleTime: REPORT_STALE_MS,
  });
}

export function useResults(tournamentId: string) {
  const { key } = useAgent();
  return useQuery({
    queryKey: ['results', key, tournamentId],
    queryFn: ({ signal }) => fetchResults(key, tournamentId, signal),
    staleTime: REPORT_STALE_MS,
  });
}

export function usePlayers(tournamentId: string) {
  const { key } = useAgent();
  return useQuery({
    queryKey: ['players', key, tournamentId],
    queryFn: ({ signal }) => fetchPlayers(key, tournamentId, signal),
    staleTime: REPORT_STALE_MS,
  });
}

export function useIssueLink() {
  const { key } = useAgent();
  return useMutation({ mutationFn: (body: IssueLinkBody) => issueLink(key, body) });
}

// Newest first, one page per fetch; the next page starts after the cursor middleware returned.
export function useWheelSpins(filters: WheelSpinsFilters, enabled = true) {
  const { key } = useAgent();
  return useInfiniteQuery({
    queryKey: ['wheel-spins', key, filters],
    queryFn: ({ pageParam, signal }) => fetchWheelSpins(key, filters, pageParam, signal),
    initialPageParam: null as string | null,
    getNextPageParam: (page) => page.nextCursor,
    staleTime: REPORT_STALE_MS,
    enabled,
  });
}

// Reads bigquery-service's Postgres mirror (refreshed every 2 hours), so re-reading costs nothing.
export function useWhales(filters: WhalesFilters) {
  const { key } = useAgent();
  return useQuery({
    queryKey: ['whales', key, filters],
    queryFn: ({ signal }) => fetchWhales(key, filters, signal),
    staleTime: REPORT_STALE_MS,
  });
}

// Admin only. The analysts rebuild the report once a night, so it is re-read at most every half
// hour; an unchanged report keeps its reference and the page does not reload the frame.
const TRAFFIC_QUALITY_STALE_MS = 30 * 60_000;

export function useTrafficQuality(enabled = true) {
  const { key } = useAgent();
  return useQuery({
    queryKey: ['traffic-quality', key],
    queryFn: ({ signal }) => fetchTrafficQuality(key, signal),
    staleTime: TRAFFIC_QUALITY_STALE_MS,
    enabled,
  });
}
